import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import Pill from '../components/Pill';
import { fmt, ago } from '../utils/format';
import { printInvoices } from '../utils/printInvoices';
import { RefreshButton } from '../hooks/useRefreshTrigger';
import ReceivedPOsSummaryPopup from './ReceivedPOsSummaryPopup';

const PAGE_VERSION = 'v3.2';

// ── Truncate helper ───────────────────────────────────────────────────────────
const trunc = (str, len) => {
  if (!str) return '—';
  return str.length > len ? str.slice(0, len) + '…' : str;
};

// ── Stage pill colours ────────────────────────────────────────────────────────
const stageColor = (stage) => {
  if (!stage) return 'muted';
  const s = stage.toLowerCase();
  if (s.includes('fully picked'))      return 'green';
  if (s.includes('approved'))          return 'green';
  if (s.includes('new'))               return 'blue';
  if (s.includes('awaiting payment'))  return 'gold';
  if (s.includes('payment received'))  return 'gold';
  if (s.includes('warehouse'))         return 'blue';
  return 'muted';
};

// ── Dispatch status ───────────────────────────────────────────────────────────
// FULL               — moved=0, all items covered by received PO SOH
// PARTIAL_DISPATCHED — some items moved>0, remaining qty covered by received PO
// PARTIAL_WAITING    — moved<ordered, some items covered by PO, not all
const dispatchStatus = (items, totalSoLines, totalMovedLines) => {
  const anyMoved    = totalMovedLines > 0;
  const outstanding = items.filter(i => Number(i.item_qty) > Number(i.item_qty_moved));
  const allCovered  = outstanding.length > 0 && outstanding.every(i =>
    Number(i.soh_avail) >= (Number(i.item_qty) - Number(i.item_qty_moved))
  );

  // If not all outstanding PO-matched items are covered → PARTIAL_WAITING
  if (!allCovered) return 'PARTIAL_WAITING';

  // All PO-matched outstanding items covered — but are there unmatched SO lines?
  // Use total_so_lines vs PO-matched lines to detect unmatched outstanding items
  const hasUnmatchedOutstanding = totalSoLines && (totalSoLines - totalMovedLines) > items.length;
  if (hasUnmatchedOutstanding) return 'PARTIAL_WAITING';

  // All outstanding items covered, some previously dispatched
  if (anyMoved) return 'PARTIAL_DISPATCHED';

  return 'FULL';
};

// ── Order items detail — fetches ALL SO items, marks PO ones ─────────────────
const OrderItemsDetail = ({ order, onStatusResolved }) => {
  const [allItems, setAllItems] = useState(null);
  const [loading, setLoading]   = useState(true);

  const poItemCodes = useMemo(
    () => new Set(order.items.map(i => i.item_code)),
    [order.items]
  );

  useEffect(() => {
    const load = async () => {
      const { data: hdr } = await supabase
        .from('sales_order_header')
        .select('order_id')
        .eq('order_ref', order.order_ref)
        .single();
      if (!hdr) { setLoading(false); return; }

      const { data: items } = await supabase
        .from('sales_order_items')
        .select('item_code, item_name, item_qty, item_qty_moved')
        .eq('order_id', hdr.order_id);

      const codes = (items || []).map(i => i.item_code).filter(Boolean);
      const { data: stock } = codes.length
        ? await supabase.from('product_stock').select('code, stock_on_hand, available').in('code', codes)
        : { data: [] };

      const stockMap = {};
      for (const s of (stock || [])) stockMap[s.code] = { soh: s.stock_on_hand, avail: s.available };

      // Build a map from item_code to po_ref from the view data
      const poRefMap = {};
      for (const vi of (order.items || [])) {
        if (vi.po_ref) poRefMap[vi.item_code] = vi.po_ref;
      }

      const enriched = (items || []).map(i => ({
        ...i,
        soh:    stockMap[i.item_code]?.soh   ?? null,
        avail:  stockMap[i.item_code]?.avail ?? null,
        po_ref: poRefMap[i.item_code] ?? null,
      }));

      setAllItems(enriched);
      setLoading(false);

      // Compute real status from ALL SO items and bubble up
      if (onStatusResolved) {
        const anyMoved    = enriched.some(i => Number(i.item_qty_moved) > 0);
        const outstanding = enriched.filter(i => Number(i.item_qty) > Number(i.item_qty_moved));
        const allCovered  = outstanding.every(i =>
          Number(i.soh ?? 0) >= (Number(i.item_qty) - Number(i.item_qty_moved))
        );
        let resolvedStatus;
        if (!outstanding.length || allCovered) {
          resolvedStatus = anyMoved ? 'PARTIAL_DISPATCHED' : 'FULL';
        } else {
          resolvedStatus = 'PARTIAL_WAITING';
        }
        onStatusResolved(order.order_ref, resolvedStatus);
      }
    };
    load();
  }, [order.order_ref]);

  if (loading) return <div style={{ padding: '12px 24px', color: 'var(--muted)', fontSize: 12 }}>Loading items…</div>;
  if (!allItems || !allItems.length) return <div style={{ padding: '12px 24px', color: 'var(--muted)', fontSize: 12 }}>No items found</div>;

  return (
    <div className="order-items-detail">
      <table className="detail-table">
        <thead>
          <tr>
            <th className="col-code">Item Code</th>
            <th className="col-po">PO</th>
            <th className="col-name">Item Name</th>
            <th className="col-ordered num">Ordered</th>
            <th className="col-moved num">Moved</th>
            <th className="col-soh num">SOH</th>
            <th className="col-avail num">Avail</th>
          </tr>
        </thead>
        <tbody>
          {allItems.map((item, i) => {
            const isFromPO = poItemCodes.has(item.item_code);
            return (
              <tr key={i}>
                <td className="col-code"><span className="code-badge code-badge--sm">{item.item_code}</span></td>
                <td className="col-po" style={{ textAlign: 'center' }}>
                  {item.po_ref && <span className="po-badge">{item.po_ref}</span>}
                </td>
                <td className="col-name product-name">{trunc(item.item_name, 40)}</td>
                <td className="col-ordered num">{fmt(item.item_qty)}</td>
                <td className="col-moved num">{fmt(item.item_qty_moved)}</td>
                <td className={`col-soh num ${Number(item.soh) <= 0 ? 'neg' : ''}`}>{fmt(item.soh)}</td>
                <td className={`col-avail num ${Number(item.avail) <= 0 ? 'neg' : ''}`}>{fmt(item.avail)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Grouped order table ───────────────────────────────────────────────────────
const ReceivedPOOpenOrders = ({ orders, loading, selected, onToggle, onToggleAll }) => {
  const [expanded, setExpanded]             = useState(new Set());
  const [resolvedStatus, setResolvedStatus] = useState({});
  const [submitted, setSubmitted]           = useState(new Set());

  if (loading) return <div className="loading-row">Loading...</div>;
  if (!orders.length) return <div className="empty-row">✓ No dispatchable orders for recently received POs</div>;

  const allSelected  = orders.every(o => selected.has(o.order_ref));
  const someSelected = orders.some(o => selected.has(o.order_ref));

  const toggleExpand = (orderRef) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(orderRef)) next.delete(orderRef); else next.add(orderRef);
      return next;
    });
  };

  const handleStatusResolved = (orderRef, status) => {
    setResolvedStatus(prev => ({ ...prev, [orderRef]: status }));
  };

  const handleFDHold = async (e, orderRef) => {
    e.stopPropagation();
    try {
      await supabase.from('sales_order_stage_updates').insert({
        order_ref: orderRef,
        stage:     'Fully Picked - Hold',
      });
      setSubmitted(prev => new Set(prev).add(orderRef));
    } catch (err) {
      alert(`Failed to queue ${orderRef}: ${err.message}`);
    }
  };

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th style={{ width: 36, textAlign: 'center' }}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                onChange={() => onToggleAll(orders)}
                title="Select all"
                style={{ cursor: 'pointer' }}
              />
            </th>
            <th style={{ width: 24 }}></th>
            <th>Order Ref</th>
            <th style={{ width: 90 }}></th>
            <th>Status</th>
            <th>Stage</th>
            <th>Customer</th>
            <th>Supplier</th>
            <th>PO Ref</th>
            <th>Received</th>
            <th className="num">Lines</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const isSelected     = selected.has(order.order_ref);
            const isExpanded     = expanded.has(order.order_ref);
            const isSubmitted    = submitted.has(order.order_ref);
            const status         = resolvedStatus[order.order_ref] || dispatchStatus(order.items, order.total_so_lines, order.total_moved_lines);
            const isDispatchable = (status === 'FULL' || status === 'PARTIAL_DISPATCHED') && order.stage !== 'Fully Picked - Hold';

            return (
              <React.Fragment key={order.order_ref}>
                <tr
                  className={`data-row ${isSelected ? 'data-row--selected' : ''} ${isExpanded ? 'data-row--expanded' : ''}`}
                  onClick={() => toggleExpand(order.order_ref)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggle(order.order_ref)}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                  <td style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 11 }}>
                    {isExpanded ? '▼' : '▶'}
                  </td>
                  <td><span className="code-badge">{order.order_ref}</span></td>
                  <td onClick={e => e.stopPropagation()}>
                    {isDispatchable && (
                      isSubmitted
                        ? <span style={{ fontSize: 10, color: 'var(--muted)', fontStyle: 'italic' }}>Submitted</span>
                        : <button
                            onClick={e => handleFDHold(e, order.order_ref)}
                            style={{
                              fontSize: 10, padding: '2px 8px', cursor: 'pointer',
                              background: 'rgba(148,103,60,0.25)', color: '#c8922a',
                              border: '1px solid rgba(200,146,42,0.35)', borderRadius: 4, fontWeight: 600,
                              letterSpacing: '0.03em',
                            }}
                          >
                            FPHold
                          </button>
                    )}
                  </td>
                  <td>
                    {status === 'FULL'               && <span className="dispatch-full">FULL · DISPATCH</span>}
                    {status === 'PARTIAL_DISPATCHED'  && <span className="dispatch-partial-dispatched">PARTIAL · DISPATCH</span>}
                    {status === 'PARTIAL_WAITING'     && <span className="dispatch-partial">PARTIAL · WAITING</span>}
                  </td>
                  <td><Pill label={order.stage || '—'} color={stageColor(order.stage)} /></td>
                  <td>{trunc(order.customer_name, 20)}</td>
                  <td><Pill label={trunc(order.supplier, 10) || '—'} color="blue" /></td>
                  <td>
                    {(order.po_refs || [order.po_ref]).map(ref => (
                      <span key={ref} className="code-badge code-badge--sm" style={{ marginRight: 3 }}>{ref}</span>
                    ))}
                  </td>
                  <td className="muted">{ago(order.fully_received_date)}</td>
                  <td className="num">{order.items.length}</td>
                </tr>

                {isExpanded && (
                  <tr className="detail-row">
                    <td colSpan={11} style={{ padding: 0 }}>
                      <OrderItemsDetail order={order} onStatusResolved={handleStatusResolved} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── ReceivedPOs Page ──────────────────────────────────────────────────────────
const ReceivedPOs = () => {
  const navigate = useNavigate();
  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [supplier, setSupplier]       = useState('');
  const [selected, setSelected]       = useState(new Set());
  const [printing, setPrinting]       = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    const { data: rows } = await supabase
      .from('v_received_po_open_orders')
      .select('*')
      .order('fully_received_date', { ascending: false });
    setData(rows || []);
    setLoading(false);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const row of data) {
      if (!map.has(row.order_ref)) {
        map.set(row.order_ref, {
          order_ref:           row.order_ref,
          stage:               row.stage,
          customer_name:       row.customer_name,
          supplier:            row.supplier,
          po_refs:             new Set(),
          po_ref:              row.po_ref,
          fully_received_date: row.fully_received_date,
          total_so_lines:      row.total_so_lines,
          total_moved_lines:   row.total_moved_lines,
          items:               [],
        });
      }
      const entry = map.get(row.order_ref);
      if (row.po_ref) entry.po_refs.add(row.po_ref);
      entry.items.push({
        item_code:      row.item_code,
        item_name:      row.item_name,
        item_qty:       row.item_qty,
        item_qty_moved: row.item_qty_moved,
        soh_avail:      row.soh_avail,
        po_ref:         row.po_ref,
      });
    }
    return [...map.values()].map(o => ({
      ...o,
      po_refs: [...o.po_refs],
    }));
  }, [data]);

  const suppliers = useMemo(() =>
    [...new Set(data.map(r => r.supplier).filter(Boolean))].sort()
  , [data]);

  const filtered = useMemo(() =>
    !supplier ? grouped : grouped.filter(o => o.supplier === supplier)
  , [grouped, supplier]);

  const handleToggle = (orderRef) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(orderRef)) next.delete(orderRef); else next.add(orderRef);
      return next;
    });
  };

  const handleToggleAll = (orders) => {
    const allSelected = orders.every(o => selected.has(o.order_ref));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) orders.forEach(o => next.delete(o.order_ref));
      else orders.forEach(o => next.add(o.order_ref));
      return next;
    });
  };

  const handlePrint = async () => {
    if (!selected.size) return;
    setPrinting(true);
    await printInvoices([...new Set([...selected])]);
    setPrinting(false);
  };

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/frontline-tools')}>← Frontline Tools</button>
        <span className="page-nav__title">Received POs & Customer Orders</span>
        <span className="app-header__refresh">Last refresh: {lastRefresh.toLocaleTimeString('en-NZ')}</span>
        <button
          onClick={() => setShowSummary(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', whiteSpace: 'nowrap',
            background: 'var(--bg3, #f0f2f5)',
            border: '1px solid var(--border2, #d0d5dd)',
            color: 'var(--text, #1a1a2e)',
          }}
        >
          Show Received POs
        </button>
        <RefreshButton triggerKey={['purchase_orders', 'stock_counts']} label="Refresh POs" onDone={load} />
        <span style={{ fontSize: 10, color: 'var(--muted)', marginLeft: 8 }}>{PAGE_VERSION}</span>
      </div>
      {showSummary && <ReceivedPOsSummaryPopup onClose={() => setShowSummary(false)} />}

      <section className="section">
        <SectionHeader
          title="Received POs — Open Customer Orders"
          count={filtered.length}
          onRefresh={load}
          loading={loading}
          actions={
            <>
              <button
                className="action-btn action-btn--close"
                onClick={handlePrint}
                disabled={printing || selected.size === 0}
                style={{ marginRight: 8, opacity: selected.size === 0 ? 0.4 : 1 }}
              >
                {printing ? '…' : `🖨 Print${selected.size > 0 ? ` ${selected.size} Invoice${selected.size !== 1 ? 's' : ''}` : ''}`}
              </button>
              <select
                value={supplier}
                onChange={e => { setSupplier(e.target.value); setSelected(new Set()); }}
                style={{
                  background: 'var(--bg3)', color: 'var(--white)',
                  border: '1px solid var(--border2)', borderRadius: 4,
                  padding: '4px 10px', fontFamily: 'var(--font-body)',
                  fontSize: 12, cursor: 'pointer', marginRight: 8,
                }}
              >
                <option value="">All Suppliers</option>
                {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </>
          }
        />
        <p className="section__desc">
          Customer orders ready to dispatch — stock received and SOH covers the order. Click a row to see items.
          {supplier && <strong style={{ color: 'var(--gold)', marginLeft: 8 }}>Filtered: {supplier}</strong>}
          {selected.size > 0 && <strong style={{ color: 'var(--green-light)', marginLeft: 8 }}>{selected.size} selected</strong>}
        </p>
        <ReceivedPOOpenOrders
          orders={filtered}
          loading={loading}
          selected={selected}
          onToggle={handleToggle}
          onToggleAll={handleToggleAll}
        />
      </section>
    </main>
  );
};

export default ReceivedPOs;
